import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const slides = [
  {
    src: "/assets/ewp1110_render1.png",
    label: "Assembled View",
    annotations: [
      { top: "20%", right: "6%", text: "Switchable Impeller", side: "right" },
      { bottom: "18%", left: "2%", text: "Fail-Safe Locked Open", side: "left" },
      { top: "55%", left: "5%", text: "Reinforced Spring", side: "left" },
    ],
  },
  {
    src: "/assets/ewp1110_render4.png",
    label: "Studio Detail",
    annotations: [
      { top: "25%", right: "4%", text: "VW · Audi · Škoda Fitment", side: "right" },
      { bottom: "22%", left: "4%", text: "OE Ref 04L 121 011", side: "left" },
    ],
  },
  {
    src: "/assets/ewp1110_render5.png",
    label: "Inlet Geometry",
    annotations: [
      { top: "30%", right: "6%", text: "Integrated Inlet Filter", side: "right" },
      { bottom: "20%", left: "3%", text: "Smooth Inlet Passage", side: "left" },
    ],
  },
  {
    src: "/assets/ewp1110_render6.png",
    label: "Cross Section",
    annotations: [
      { top: "22%", right: "5%", text: "Back-face Vanes", side: "right" },
      { bottom: "24%", left: "4%", text: "Spring Retainer", side: "left" },
    ],
  },
  {
    src: "/assets/ewp1110_render2.png",
    label: "Exploded Components",
    annotations: [
      { top: "28%", right: "6%", text: "Proprietary Geometry", side: "right" },
      { bottom: "22%", left: "4%", text: "Open-State Bias", side: "left" },
    ],
  },
  {
    src: "/assets/ewp1110_render3.png",
    label: "Internal Detail",
    annotations: [
      { top: "30%", right: "6%", text: "Hydraulic Actuation", side: "right" },
      { bottom: "30%", left: "4%", text: "Coolant Flow Path", side: "left" },
    ],
  },
  {
    src: "/assets/ewp1110_render7.png",
    label: "Rear Detail",
    annotations: [
      { top: "25%", right: "5%", text: "Flexible Impeller", side: "right" },
      { bottom: "25%", left: "5%", text: "Debris-Shielded", side: "left" },
    ],
  },
];

export default function HeroSlider() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (n) => setIdx((p) => (n + slides.length) % slides.length),
    []
  );
  const next = useCallback(() => go(idx + 1), [idx, go]);
  const prev = useCallback(() => go(idx - 1), [idx, go]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next, paused]);

  // keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const current = slides[idx];

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-slider"
    >
      <div className="relative aspect-square w-full max-w-[640px] mx-auto">
        {/* glow */}
        <div className="hero-glow" />

        {/* slides */}
        <div className="absolute inset-0">
          {slides.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={`MultiParts MWP1110 — ${s.label}`}
              data-testid={`hero-slide-${i}`}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ease-out ${
                i === idx ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
              loading={i === 0 ? "eager" : "lazy"}
            />
          ))}
        </div>

        {/* annotations — render only current slide's */}
        <div className="absolute inset-0 z-20 pointer-events-none">
          {current.annotations.map((a, i) => (
            <Annotation key={`${idx}-${i}`} {...a} delay={i * 120} />
          ))}
        </div>

        {/* arrow controls */}
        <button
          onClick={prev}
          aria-label="Previous render"
          data-testid="hero-slider-prev"
          className="absolute left-2 lg:-left-2 top-1/2 -translate-y-1/2 z-30 w-11 h-11 flex items-center justify-center border border-[#C8D3DF] bg-white/80 text-[#1A2232] hover:bg-[#5B9BD5] hover:text-white hover:border-[#5B9BD5] transition-all"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={next}
          aria-label="Next render"
          data-testid="hero-slider-next"
          className="absolute right-2 lg:-right-2 top-1/2 -translate-y-1/2 z-30 w-11 h-11 flex items-center justify-center border border-[#C8D3DF] bg-white/80 text-[#1A2232] hover:bg-[#5B9BD5] hover:text-white hover:border-[#5B9BD5] transition-all"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* slide label */}
      <div className="mt-1 text-center font-mono text-[10px] tracking-[0.3em] text-[#5B9BD5] uppercase">
        {String(idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")} · {current.label}
      </div>
      <div className="mt-1 text-center font-mono text-[10px] tracking-[0.3em] text-[#8896A6] uppercase">
        MPS-MWP1110 · OE REF 04L 121 011
      </div>

      {/* dots */}
      <div className="mt-4 flex items-center justify-center gap-2 z-20 relative">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => go(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-slider-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${
              i === idx
                ? "w-8 bg-[#5B9BD5]"
                : "w-1.5 bg-[#C8D3DF] hover:bg-[#536073]"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function Annotation({ top, bottom, left, right, text, side, delay = 0 }) {
  const sideRight = side === "right";
  return (
    <div
      className="absolute z-20 hidden md:flex flex-col"
      style={{
        top,
        bottom,
        left,
        right,
        alignItems: sideRight ? "flex-end" : "flex-start",
        animation: `fadeUp 0.6s ease-out ${delay}ms both`,
      }}
    >
      <span className="font-mono text-[10px] tracking-[0.2em] text-[#5B9BD5] uppercase">
        {text}
      </span>
      <div className="flex items-center gap-1.5 mt-1">
        {sideRight ? (
          <>
            <div className="h-px w-16 lg:w-20 bg-[#5B9BD5]/70" />
            <span className="w-2 h-2 bg-[#5B9BD5] rotate-45 inline-block shrink-0" />
          </>
        ) : (
          <>
            <span className="w-2 h-2 bg-[#5B9BD5] rotate-45 inline-block shrink-0" />
            <div className="h-px w-16 lg:w-20 bg-[#5B9BD5]/70" />
          </>
        )}
      </div>
    </div>
  );
}
